import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { XCircle } from 'lucide-react';
import { instructorApi } from '../../api/instructor';
import { InstructorLesson } from '../../types';
import { isOfferedOpenSlot } from '../../lib/openSlot';
import { Button, ConfirmDialog, TextField, useToast, type MenuEntry } from '../charte';
import { Sheet } from '../shell/Sheet';
import { toLocalInput } from './useLessonActions';

// Créneaux à réserver ouverts par le moniteur lui-même (29/10/2026), quand l'auto-école le permet à ses moniteurs
// (Réglages › App moniteur, « Autoriser les moniteurs à ouvrir et fermer des créneaux disponibles à la réservation ») :
// « Ouvrir un créneau » dans Mon planning, « Retirer le créneau » sur un créneau encore libre. Le serveur revérifie le
// réglage, l'horaire et les chevauchements.

const failure = (e: unknown, fallback: string) => (e instanceof Error && e.message) || fallback;

/** Réglage de l'auto-école, lu avec les autres réglages de l'application ; sans réponse du serveur, rien n'est proposé. */
export function useOpenSlotsAllowed(): boolean {
  const settings = useQuery({ queryKey: ['instructor-app-settings'], queryFn: instructorApi.getAppSettings, staleTime: 60_000 });
  return settings.data?.openSlots?.enabled === true;
}

const refreshPlanning = (client: ReturnType<typeof useQueryClient>) => {
  void client.invalidateQueries({ queryKey: ['planning'] });
  void client.invalidateQueries({ queryKey: ['today'] });
};

/** Prochaine heure pleine, et une heure après : horaire proposé à l'ouverture de la fenêtre. */
function nextHour() {
  const start = new Date();
  start.setHours(start.getHours() + 1, 0, 0, 0);
  return { startsAt: toLocalInput(start.toISOString()), endsAt: toLocalInput(new Date(start.getTime() + 3_600_000).toISOString()) };
}

/** Fenêtre « Ouvrir un créneau » : début et fin, puis « Ouvrir le créneau ». */
export function OpenSlotSheet({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState(nextHour);
  const [error, setError] = useState<string | null>(null);
  // Une demande par horaire : un double appui ou une reprise après coupure n'ouvre qu'un créneau ; un autre horaire
  // est une autre demande.
  const request = useRef<{ signature: string; id: string } | null>(null);
  const open = useMutation({
    mutationFn: () => {
      const signature = `${form.startsAt}|${form.endsAt}`;
      if (request.current?.signature !== signature) request.current = { signature, id: crypto.randomUUID() };
      return instructorApi.openSlot({
        requestId: request.current.id,
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
      });
    },
    onSuccess: () => {
      refreshPlanning(client);
      toast('Créneau ouvert : vos élèves peuvent le réserver.');
      onClose();
    },
    onError: (e) => setError(failure(e, 'Ouverture du créneau impossible.')),
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (new Date(form.endsAt).getTime() <= new Date(form.startsAt).getTime()) {
      setError('L’heure de fin doit suivre l’heure de début.');
      return;
    }
    open.mutate();
  };
  return (
    <Sheet
      title="Ouvrir un créneau"
      description="Vos élèves le voient dans leur espace et peuvent le réserver ; ils sont prévenus."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Retour
          </Button>
          <Button type="submit" form="ouvrir-creneau" variant="primary" className="ml-auto" loading={open.isPending}>
            {open.isPending ? 'Ouverture…' : 'Ouvrir le créneau'}
          </Button>
        </>
      }
    >
      <form id="ouvrir-creneau" onSubmit={onSubmit} className="space-y-4">
        <TextField
          label="Début"
          type="datetime-local"
          required
          value={form.startsAt}
          onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
          className="h-11 text-[15px]"
        />
        <TextField
          label="Fin"
          type="datetime-local"
          required
          value={form.endsAt}
          onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))}
          className="h-11 text-[15px]"
        />
        <p className="text-xs text-secondary">De 15 minutes à 4 heures, sans chevaucher vos leçons ni vos absences.</p>
        {error && (
          <p role="alert" className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
            {error}
          </p>
        )}
      </form>
    </Sheet>
  );
}

/** « Retirer le créneau » d'un créneau encore libre et à venir, si l'auto-école le permet. */
export function useCloseOpenSlot(lesson: InstructorLesson, allowed: boolean): { entries: MenuEntry[]; dialogs: ReactNode } {
  const client = useQueryClient();
  const toast = useToast();
  const [asking, setAsking] = useState(false);
  const close = useMutation({
    mutationFn: () => instructorApi.closeOpenSlot(lesson.id),
    onSuccess: () => {
      refreshPlanning(client);
      setAsking(false);
      toast('Créneau retiré.');
    },
  });
  if (!allowed || !isOfferedOpenSlot(lesson)) return { entries: [], dialogs: null };
  return {
    entries: [{ label: 'Retirer le créneau', icon: XCircle, danger: true, onSelect: () => setAsking(true) }],
    dialogs: (
      <ConfirmDialog
        open={asking}
        title="Retirer ce créneau ?"
        tone="danger"
        confirmLabel="Retirer le créneau"
        pending={close.isPending}
        error={close.isError ? failure(close.error, 'Retrait impossible.') : undefined}
        onConfirm={() => close.mutate()}
        onCancel={() => {
          setAsking(false);
          close.reset();
        }}
      >
        Les élèves ne pourront plus le réserver.
      </ConfirmDialog>
    ),
  };
}
